'use client'
import AddToCollectionSidebar from '@/app/components/collections/add-to-collection-panel/sidebar'
import CloseIcon from '@/app/icons/close'
import EditPanel from 'edit'
import { SIDEBAR_WIDTH } from 'edit/panel/constants'
import { EditPanelGif } from 'edit/panel/types/gif'
import { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useKeyPress } from 'react-use'
import { GifPermissions } from 'types'
import UserContext from '../../context/user-context'
import { clearBulkEdit, useBulkEditGifs, useBulkEditOpen } from '../grid/bulk-edit-state'

type Props = {
    permissions: GifPermissions
    gifs: EditPanelGif[]
    onClose: () => void
}

const EditPanelSidebar = ({ permissions, gifs, onClose }: Props) => {
    const { user } = useContext(UserContext)
    const showPanel = useBulkEditOpen()
    const bulkEditGifs = useBulkEditGifs()
    const [isEsc] = useKeyPress('Escape')
    const [currentOverflow, setCurrentOverflow] = useState<string>('')
    const escPressedRef = useRef(false)

    useEffect(() => {
        if (isEsc && !escPressedRef.current) {
            escPressedRef.current = true
            const selectedGifs = Object.keys(bulkEditGifs)
            if (selectedGifs.length > 0) {
                clearBulkEdit()
            } else {
                onClose()
            }
            setTimeout(() => {
                escPressedRef.current = false
            }, 100)
        }
    }, [isEsc, onClose])
    useEffect(() => {
        setCurrentOverflow(document.body.style.overflow)
        if (showPanel) {
            window.scrollTo(0, 0)
            document.body.scrollTop = 0
            document.body.style.overflow = 'hidden'
        }
        return () => {
            document.body.style.overflow = currentOverflow
        }
    }, [currentOverflow, showPanel])

    return showPanel
        ? createPortal(
              <div
                  className="fixed top-0 right-0 bottom-0 z-modal flex h-full overflow-y-auto bg-giphyBlack"
                  style={{ width: SIDEBAR_WIDTH }}
              >
                  {gifs?.length > 0 ? (
                      <EditPanel
                          closeable
                          className="mx-4 my-1 flex-1"
                          colorScheme="panel"
                          gifs={gifs}
                          loading={false}
                          collectionsComponent={<AddToCollectionSidebar channelId={user?.channel_id!} gifs={gifs} />}
                          permissions={{
                              altText: false,
                              cta: !!user?.is_verified,
                              delete: permissions.delete,
                              featureTag: permissions.feature_tags,
                              languageTags: !!user?.permissions.edit_language_tags,
                              metadata: permissions.extended_metadata,
                              privacy: permissions.hide,
                              rate: permissions.rate,
                              staff: !!user?.is_staff,
                              tag: permissions.tag,
                              tagCounts: permissions.tag_counts,
                              takedown: !!user?.permissions.can_takedown_content,
                          }}
                          onClose={() => {
                              onClose()
                          }}
                      />
                  ) : (
                      <div className="flex flex-col bg-giphyDarkestGrey">
                          <div className="flex justify-end p-4">
                              <CloseIcon className="size-8 fill-giphyWhite" onClick={() => onClose()} />
                          </div>
                          <div className="mx-4 my-40 flex flex-col items-center justify-center p-2">
                              <div className="relative mb-6 bg-giphyBlack px-6 py-4 text-center font-bold text-pretty text-giphyLightGrey">
                                  <div className="absolute top-[-100px] right-0 left-0 flex justify-center">
                                      <img
                                          src="https://giphy.com/static/img/PEEKERS_DIDYOUMEAN.gif"
                                          width="165"
                                          height="198"
                                          alt=""
                                      />
                                  </div>
                                  <p className="px-4 text-lg">You haven&apos;t selected anything for editing yet!</p>
                              </div>
                              <pre className="text-[15px] text-pretty">
                                  <ol>
                                      <li>Click to select GIFs</li>
                                      <li>Cmd + Click to multiselect</li>
                                      <li>Escape to close the panel</li>
                                  </ol>
                              </pre>
                          </div>
                      </div>
                  )}
              </div>,
              document.body
          )
        : null
}

export default EditPanelSidebar
